// Português (Portugal) — as frases próprias do LowFodmaps. Tipado `ProductStrings`:
// uma chave em falta ou a mais falha a verificação de tipos.
//
// Tom: simples e gentil, tratamento por "você" implícito. Sem promessas de
// cura; sempre "normalmente", nunca "seguro".

import type { ProductStrings } from './en'

export const pt: ProductStrings = {
  auth: {
    emailPlaceholder: 'nome@exemplo.com',
    heroTitle: 'Um intestino mais calmo, um alimento de cada vez.',
    heroSubline:
      'Consulte qualquer alimento, siga um plano de reintrodução passo a passo e mantenha um diário simples do que comeu e de como se sentiu.',
  },
  language: {
    hint: 'Menus, datas e nomes dos alimentos.',
  },
  nav: {
    today: 'Hoje',
    foods: 'Alimentos',
    plan: 'Plano',
  },
  levels: {
    low: 'Baixo',
    moderate: 'Moderado',
    high: 'Alto',
  },
  groups: {
    fructans: 'Frutanos',
    gos: 'GOS',
    lactose: 'Lactose',
    fructose: 'Frutose',
    sorbitol: 'Sorbitol',
    mannitol: 'Manitol',
  },
  scores: {
    label: 'Como está o seu intestino?',
    s0: 'Bem',
    s1: 'Ligeiro',
    s2: 'Incomoda',
    s3: 'Mal',
    s4: 'Muito mal',
  },
  disclaimer:
    'Um guia geral, não um conselho médico. As porções contam e a investigação continua a evoluir. Se puder, faça esta dieta com um nutricionista — e consulte um médico antes de começar se os sintomas forem recentes.',
  foods: {
    title: 'Tabela de alimentos',
    subtitle: 'As classificações são para uma porção normal. {count} alimentos, e a crescer.',
    search: 'Procurar um alimento…',
    allLevels: 'Todos',
    allCategories: 'Todos os alimentos',
    anyGroup: 'Qualquer FODMAP',
    showing: '{n} alimentos',
    none: 'Nada corresponde. Experimente uma palavra mais curta ou limpe os filtros.',
    clear: 'Limpar filtros',
    colFood: 'Alimento',
    colLevel: 'FODMAP',
    colWhy: 'A ter em conta',
    nothingToWatch: 'Nada em particular',
    legendTitle: 'Como ler a tabela',
    legendLow: 'Normalmente tolerado numa porção normal.',
    legendModerate: 'Uma porção pequena costuma correr bem; uma inteira talvez não.',
    legendHigh: 'A evitar durante a eliminação. Teste na reintrodução.',
    categories: {
      vegetables: 'Legumes',
      fruit: 'Fruta',
      grains: 'Pão e cereais',
      protein: 'Carne, peixe, ovos, tofu',
      dairy: 'Laticínios e alternativas',
      legumes: 'Feijão e lentilhas',
      nuts: 'Frutos secos e sementes',
      sweets: 'Açúcar e doces',
      drinks: 'Bebidas',
      pantry: 'Óleos, molhos, temperos',
    },
    notes: {
      smallOk: 'Uma porção pequena costuma correr bem.',
      largeAdds: 'Bem numa porção normal; as grandes acumulam.',
      ripeness: 'Firme e pouco madura é baixa. Muito madura, com pintas, é alta.',
      greenOnly: 'Só a parte verde. A parte branca é alta.',
      cannedRinsed: 'De lata e bem passado por água, em porção pequena.',
      checkLabel: 'Veja no rótulo se tem cebola, alho, mel, inulina ou trigo.',
      firmOnly: 'Firme e prensado. O tofu sedoso é alto.',
      infused: 'Os FODMAP não se dissolvem em gordura: fica o sabor sem eles.',
      alcohol: 'Baixo em FODMAP, mas o álcool em si pode irritar o intestino.',
      soyProtein: 'Leia os ingredientes: "proteína de soja" é baixo, "grão de soja" é alto.',
      weakBrew: 'Fraco e com pouca infusão. O chá preto forte é mais alto.',
    },
  },
  publicTable: {
    kicker: 'Tabela low-FODMAP gratuita',
    title: 'Posso comer isto?',
    lede: 'Procure entre {count} alimentos do dia a dia. Verde costuma correr bem, amarelo em porções pequenas, vermelho é para deixar de fora por agora.',
    ctaTitle: 'Já sabe o que evitar. Descubra agora o que pode voltar a comer.',
    ctaBody:
      'O LowFodmaps guia-o na reintrodução, um FODMAP de cada vez, e guarda um diário do que comeu e de como se sentiu — para chegar à sua própria lista, não a uma genérica.',
    cta: 'Experimentar o LowFodmaps {n} dias grátis',
    signIn: 'Iniciar sessão',
  },
  today: {
    title: 'Hoje',
    previousDay: 'Dia anterior',
    nextDay: 'Dia seguinte',
    backToToday: 'Voltar a hoje',
    whatDidYouEat: 'O que comeu?',
    placeholder: 'Arroz, frango grelhado, cenoura…',
    add: 'Adicionar',
    emptyDay: 'Ainda não há nada registado neste dia.',
    remove: 'Remover',
    noteLabel: 'Alguma coisa a lembrar?',
    notePlaceholder: 'Inchaço depois do almoço, dormi mal, dia de stress…',
    saveNote: 'Guardar nota',
    saved: 'Guardado',
    weekTitle: 'Os seus últimos 7 dias',
    weekEmpty: 'Registe como se sente todos os dias e o padrão começa a aparecer aqui.',
    notLogged: 'Sem registo',
    meals: {
      breakfast: 'Pequeno-almoço',
      lunch: 'Almoço',
      dinner: 'Jantar',
      snack: 'Lanche',
      drink: 'Bebida',
    },
    phaseCard: {
      elimination: 'Eliminação · dia {n}',
      eliminationNoDate: 'Fase de eliminação',
      reintroduction: 'Reintrodução · {done} de {total} testados',
      personalization: 'A sua dieta',
      openPlan: 'Abrir o plano',
      activeChallenge: 'Em teste: {name}',
    },
  },
  plan: {
    title: 'O seu plano',
    subtitle: 'Três fases. A maioria das pessoas demora oito a doze semanas no total.',
    youAreHere: 'Está aqui',
    moveHere: 'Estou nesta fase',
    phases: {
      elimination: {
        name: '1. Eliminação',
        length: '2 a 6 semanas',
        body: 'Troque os alimentos ricos em FODMAP por alternativas baixas e deixe o intestino acalmar. É um teste curto, não uma forma de comer para sempre: se ao fim de seis semanas não se sentir melhor, os FODMAP provavelmente não são o seu problema — pare e fale com um profissional.',
      },
      reintroduction: {
        name: '2. Reintrodução',
        length: '6 a 8 semanas',
        body: 'Mantenha a dieta de eliminação e teste um grupo de FODMAP de cada vez: o mesmo alimento em três dias, um pouco mais em cada dia. Depois descanse três dias e passe ao seguinte.',
      },
      personalization: {
        name: '3. A sua dieta',
        length: 'Daí em diante',
        body: 'Volte a comer tudo o que tolerou, mantenha porções pequenas do que tolerou a meio e volte a testar o resto de poucos em poucos meses. A tolerância muda.',
      },
    },
    startedOn: 'A eliminação começou em',
    dayN: 'Dia {n} da eliminação',
    readyHint: 'Os sintomas acalmaram há uma semana ou mais? Pode começar a reintroduzir.',
    challengesTitle: 'Testes de reintrodução',
    challengesHint:
      'Um de cada vez, pela ordem que quiser — muita gente começa pelos alimentos de que tem mais saudades. Mantenha tudo o resto baixo em FODMAP enquanto testa.',
    progress: '{done} de {total} testados',
    start: 'Começar este teste',
    testFood: 'Alimento de teste',
    day: 'Dia {n}',
    portion: 'Porção',
    howWasIt: 'Como correu?',
    dayNotePlaceholder: 'Nota opcional',
    notDoneYet: 'Por fazer',
    restFirst: 'Terminou? Descanse {n} dias na dieta de eliminação antes do teste seguinte.',
    stopEarly: '“Incomoda” ou pior? Pare aí — não precisa de subir para a porção seguinte.',
    verdictTitle: 'O seu veredicto',
    suggested: 'sugerido',
    reset: 'Recomeçar',
    resetConfirm: 'Limpar este teste e as suas notas?',
    oneAtATime: 'Termine “{name}” antes de começar outro.',
    status: {
      not_started: 'Por testar',
      in_progress: 'Em teste',
      tolerated: 'Tolerado',
      limited: 'Porções pequenas',
      avoid: 'Evitar por agora',
    },
    verdictHelp: {
      tolerated: 'Sem problemas a sério. Pode voltar a comer.',
      limited: 'Bem até à porção maior. Mantenha porções pequenas.',
      avoid: 'Problemas logo de início. Deixe de fora e volte a testar dentro de uns meses.',
    },
    challenges: {
      lactose: {
        name: 'Lactose',
        found: 'Leite, iogurte, gelado, queijo fresco',
        food: 'Leite de vaca',
        day1: '½ copo (125 ml)',
        day2: '1 copo (250 ml)',
        day3: '1½ copos (375 ml)',
      },
      fructose: {
        name: 'Frutose',
        found: 'Mel, manga, espargos, agave',
        food: 'Mel',
        day1: '1 colher de chá',
        day2: '2 colheres de chá',
        day3: '1 colher de sopa',
      },
      sorbitol: {
        name: 'Sorbitol',
        found: 'Abacate, amoras, fruta de caroço, pastilhas sem açúcar',
        food: 'Abacate',
        day1: '¼ de abacate',
        day2: '½ abacate',
        day3: '1 abacate',
      },
      mannitol: {
        name: 'Manitol',
        found: 'Cogumelos, couve-flor, aipo, batata-doce',
        food: 'Cogumelos brancos',
        day1: '2 cogumelos',
        day2: '4 cogumelos',
        day3: '8 cogumelos',
      },
      fructans_wheat: {
        name: 'Frutanos — trigo',
        found: 'Pão, massa, cuscuz, bolachas',
        food: 'Pão de trigo',
        day1: '1 fatia',
        day2: '2 fatias',
        day3: '3 fatias',
      },
      fructans_onion: {
        name: 'Frutanos — cebola',
        found: 'Cebola, alho-francês, chalota, quase todos os molhos prontos',
        food: 'Cebola cozinhada',
        day1: '1 colher de sopa',
        day2: '2 colheres de sopa',
        day3: '¼ de cebola',
      },
      fructans_garlic: {
        name: 'Frutanos — alho',
        found: 'Alho, alho em pó, quase todos os molhos prontos',
        food: 'Alho cozinhado',
        day1: '¼ de dente',
        day2: '½ dente',
        day3: '1 dente',
      },
      gos: {
        name: 'GOS',
        found: 'Feijão, lentilhas, grão-de-bico, cajus, pistácios',
        food: 'Grão-de-bico de lata, passado por água',
        day1: '2 colheres de sopa',
        day2: '4 colheres de sopa',
        day3: '½ chávena',
      },
    },
  },
  consent: {
    title: 'Antes de começar a registar',
    intro:
      'O LowFodmaps funciona guardando o que escreve: o que come, como se sente e como correm os seus testes de alimentos. Isto é informação sobre a sua saúde, por isso perguntamos primeiro.',
    data: 'Aceito que o LowFodmaps guarde os registos de alimentação, sintomas e testes que eu decidir fazer, para mos poder mostrar. Mais ninguém os vê, nunca são usados para publicidade, e posso descarregá-los ou apagá-los quando quiser.',
    notMedical:
      'Compreendo que o LowFodmaps é uma ferramenta de registo, não um médico nem um nutricionista. Não presta aconselhamento médico, e tenho 18 anos ou mais.',
    readMore: 'Os detalhes:',
    agree: 'Aceitar e continuar',
    withdraw: 'Pode mudar de ideias mais tarde: Definições → Apagar o que registei.',
  },
  settings: {
    deleteTitle: 'Apagar o que registei',
    deleteHint: 'Elimina o diário, os registos diários, os testes de alimentos e o plano. Retira também o consentimento para os guardar. Não pode ser desfeito.',
    delete: 'Apagar todos os meus registos',
    deleting: 'A apagar…',
    deleted: 'Tudo o que registou foi apagado.',
    deleteConfirm: 'Apagar tudo o que registou no LowFodmaps? Não pode ser desfeito.',
    deleteAccount: 'Para encerrar também a conta, cancele em Faturação e escreva para hello@mudacorp.com — eliminamo-la no prazo de 30 dias.',
    dataTitle: 'Os seus dados',
    dataHint: 'Tudo o que registou, num só ficheiro. É seu.',
    export: 'Descarregar os meus dados',
    exporting: 'A preparar…',
    exportError: 'Não foi possível preparar o ficheiro. Tente novamente.',
  },
}
